import { createContext, useContext, useRef, useState, useEffect, useCallback, useMemo } from 'react';

const AudioPlayerContext = createContext(null);

// If ended fires within this many ms of play, it's a ghost (iOS WebKit bug)
const GHOST_END_THRESHOLD_MS = 300;

// Amplitude of the Bluetooth keep-alive noise floor (≈ -62 dBFS): quiet
// enough to stay inaudible, loud enough that neither the device's silence
// detector nor the lossy BT codec (SBC/AAC) quantizes it back to "silence".
// There's no universal minimum — it's device-dependent — so this is a
// conservative practical floor, not a value to push lower. Raise it if some
// devices still sleep; going much quieter risks defeating the keep-alive.
const KEEPALIVE_AMPLITUDE = 0.0008;

export const AudioPlayerProvider = ({ children, audioSrc, isAutoplay, onPlayEnd, onPlay, keepAwake = false }) => {
  const audioRef = useRef(null);
  const animationFrameRef = useRef(null);
  const onPlayEndRef = useRef(onPlayEnd);
  const onPlayRef = useRef(onPlay);
  const hasLoadedRef = useRef(false);
  const lastAutoplayedSrcRef = useRef(null);
  const playStartTimeRef = useRef(0); // timestamp of last play event
  const retryCountRef = useRef(0);    // prevent infinite retry loops
  const audioSpeedRef = useRef(parseFloat(localStorage.getItem('universalAudioSpeed')) || 1.0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [smoothedProgress, setSmoothedProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioSpeed, setAudioSpeedState] = useState(() =>
    parseFloat(localStorage.getItem('universalAudioSpeed')) || 1.0
  );
  const [isBuffering, setIsBuffering] = useState(true);

  // Keep callback refs in sync
  useEffect(() => { onPlayEndRef.current = onPlayEnd; }, [onPlayEnd]);
  useEffect(() => { onPlayRef.current = onPlay; }, [onPlay]);

  const setAudioSpeed = useCallback((speed) => {
    const newSpeed = Math.max(0.5, Math.min(1.5, speed));
    localStorage.setItem('universalAudioSpeed', newSpeed.toString());
    audioSpeedRef.current = newSpeed;
    window.dispatchEvent(new CustomEvent('audioSpeedChange', { detail: newSpeed }));
    setAudioSpeedState(newSpeed);
  }, []);

  useEffect(() => {
    const handleGlobalSpeedChange = (event) => {
      audioSpeedRef.current = event.detail;
      setAudioSpeedState(event.detail);
    };
    window.addEventListener('audioSpeedChange', handleGlobalSpeedChange);
    return () => window.removeEventListener('audioSpeedChange', handleGlobalSpeedChange);
  }, []);

  const playSoundFromStart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    retryCountRef.current = 0;
    // Restarting while already playing doesn't fire a new 'play' event, so the
    // 'play' handler won't count it — fire onPlay here so the replay is counted.
    if (!audio.paused && onPlayRef.current) onPlayRef.current();
    audio.currentTime = 0;
    audio.play().catch(console.error);
  }, []);

  // Create ONE persistent audio element on mount — never recreated
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const updateProgress = () => {
      if (audio.duration && audio.duration > 0) {
        const pct = (audio.currentTime / audio.duration) * 100;
        if (!isNaN(pct)) {
          setProgress(pct);
          setSmoothedProgress((prev) => prev + (pct - prev) * 0.3);
          setCurrentTime(audio.currentTime);
        }
      }
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    const handlePlay = () => {
      playStartTimeRef.current = Date.now();
      setIsPlaying(true);
      if (onPlayRef.current) onPlayRef.current();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    const handlePause = () => {
      setIsPlaying(false);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    };

    const handleEnded = () => {
      // iOS WebKit ghost-ended detection: the browser can lose decoded audio
      // data while readyState stays at HAVE_ENOUGH_DATA. play() resolves
      // successfully but fires ended immediately (~100-300ms). When detected,
      // force a full src reload and retry playback.
      const timeSincePlay = Date.now() - playStartTimeRef.current;
      const isGhostEnd = timeSincePlay < GHOST_END_THRESHOLD_MS && audio.currentTime < 0.5;

      if (isGhostEnd && retryCountRef.current < 2) {
        retryCountRef.current++;

        setIsPlaying(false);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;

        // Force reload and replay
        const src = audio.src;
        audio.src = '';
        audio.removeAttribute('src');
        audio.load();
        audio.src = src;
        audio.load();

        let settled = false;
        const onReady = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          audio.removeEventListener('canplaythrough', onReady);
          audio.removeEventListener('canplay', onReady);
          audio.currentTime = 0;
          audio.play().catch(console.error);
        };
        const timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('canplaythrough', onReady);
          audio.removeEventListener('canplay', onReady);
        }, 5000);
        audio.addEventListener('canplaythrough', onReady);
        audio.addEventListener('canplay', onReady);
        return;
      }

      setIsPlaying(false);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      setProgress(100);
      setSmoothedProgress(100);
      setTimeout(() => {
        setProgress(0);
        setSmoothedProgress(0);
        setCurrentTime(0);
        if (onPlayEndRef.current) onPlayEndRef.current();
      }, 50);
    };

    const handleWaiting = () => {
      if (!hasLoadedRef.current) setIsBuffering(true);
    };

    const handleCanPlay = () => {
      audio.playbackRate = audioSpeedRef.current;
      setIsBuffering(false);
      hasLoadedRef.current = true;
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      audio.pause();
      audio.removeAttribute('src');
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audioRef.current = null;
    };
  }, []); // Mount only — single element for entire provider lifetime

  // Update playback rate
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = audioSpeed;
  }, [audioSpeed]);

  // When audioSrc changes, update the SAME element's src (no new element)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Stop current playback and reset UI
    audio.pause();
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsPlaying(false);
    setProgress(0);
    setSmoothedProgress(0);
    setCurrentTime(0);
    retryCountRef.current = 0;

    if (audioSrc) {
      hasLoadedRef.current = false;
      lastAutoplayedSrcRef.current = null;
      setIsBuffering(true);
      audio.src = audioSrc;
      audio.load();
    } else {
      audio.removeAttribute('src');
      setIsBuffering(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc]);

  // Autoplay when ready — but only once per src
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isAutoplay && !isBuffering && audio.readyState >= 3) {
      const currentSrc = audio.src;
      if (currentSrc && currentSrc !== lastAutoplayedSrcRef.current) {
        lastAutoplayedSrcRef.current = currentSrc;
        retryCountRef.current = 0;
        audio.currentTime = 0;
        audio.play().catch(() => {}); // Silent — autoplay may be blocked by browser
      }
    }
  }, [isAutoplay, isBuffering]);

  // ── Bluetooth keep-alive ────────────────────────────────────────────────
  // Some Bluetooth (and other) output devices drop into a low-power idle
  // state after a few seconds of silence. When the next clip starts, the
  // device fades in as it wakes, eating the first fraction of a second of
  // audio (the "softened start" some users report). Streaming a continuous,
  // near-inaudible noise floor keeps the link active so playback starts
  // cleanly. Opt-in via session settings: there's no benefit (and a tiny
  // battery/hiss cost) on wired or speaker output, and we can't detect
  // Bluetooth output from the browser.
  useEffect(() => {
    if (!keepAwake) return;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return; // Web Audio unsupported — nothing we can do

    const ctx = new Ctx();

    // One second of low-amplitude white noise, looped forever. Pure digital
    // silence does NOT reliably keep devices awake — the signal must carry
    // real (if tiny) energy across the audible band.
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * KEEPALIVE_AMPLITUDE;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(ctx.destination);
    source.start();

    // AudioContext may start suspended until a user gesture. Try immediately,
    // then fall back to resuming on the first interaction.
    const removeGestureListeners = () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      window.removeEventListener('touchstart', onGesture);
    };
    const onGesture = () => {
      ctx.resume().finally(() => {
        if (ctx.state === 'running') removeGestureListeners();
      });
    };
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    window.addEventListener('touchstart', onGesture);

    return () => {
      removeGestureListeners();
      try { source.stop(); } catch (_) { /* already stopped */ }
      source.disconnect();
      ctx.close().catch(() => {});
    };
  }, [keepAwake]);

  const value = useMemo(
    () => ({
      isBuffering,
      isPlaying,
      progress,
      smoothedProgress,
      currentTime,
      audioSpeed,
      setAudioSpeed,
      playSound: playSoundFromStart,
      audioElement: audioRef.current,
    }),
    [isBuffering, isPlaying, progress, smoothedProgress, currentTime, audioSpeed, setAudioSpeed, playSoundFromStart]
  );

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};
